import { cva, VariantProps } from "class-variance-authority";
import { ComponentProps, useContext } from "react";
import { DrawerSelectContext } from "../drawer-select.context";
import styles from "./drawer-select-button-trigger.module.css";

const css = cva([styles.Button], {
  variants: {
    size: {
      small: styles.Small,
      medium: styles.Medium,
      large: styles.Large,
    },
    hasValue: {
      true: styles.HasValue,
      false: styles.NoValue,
    },
    hasFocus: {
      true: styles.HasFocus,
      false: styles.NoFocus,
    },
    disabled: {
      true: styles.Disabled,
      false: styles.Enabled,
    },
  },
  defaultVariants: {
    size: "medium",
  },
});

export type DrawerSelectButtonTriggerProps = ComponentProps<"button"> &
  VariantProps<typeof css> & {
    label?: string;
    displayChevron?: boolean;
  };

export function DrawerSelectButtonTrigger({
  className,
  size,
  label,
  displayChevron = true,
  disabled = false,
  ...props
}: DrawerSelectButtonTriggerProps) {
  const {
    value = [],
    isOpen,
    handleClearValue,
  } = useContext(DrawerSelectContext);

  const valueLabel = value.map((v) => v.text).join(", ");
  const hasValue = value.length > 0;

  const handleClearClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      handleClearValue();
    }
  };

  return (
    <button
      type="button"
      className={css({ className, size, hasValue, hasFocus: isOpen, disabled })}
      disabled={disabled}
      {...props}
    >
      <div className={styles.ButtonText}>
        {label && <span className={styles.Label}>{label}</span>}
        {hasValue && <span className={styles.Value}>{valueLabel}</span>}
      </div>

      {displayChevron && hasValue && (
        <span
          className={styles.ClearIcon}
          onClick={handleClearClick}
          onPointerDown={(e) => e.stopPropagation()}
          role="button"
          tabIndex={-1}
          aria-label="Limpar seleção"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 10.586l4.95-4.95a1 1 0 111.414 1.414L13.414 12l4.95 4.95a1 1 0 01-1.414 1.414L12 13.414l-4.95 4.95a1 1 0 01-1.414-1.414L10.586 12l-4.95-4.95a1 1 0 111.414-1.414L12 10.586z"
            />
          </svg>
        </span>
      )}

      {displayChevron && !hasValue && (
        <span className={styles.Icon}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M17.7072 10.7072L12.7073 15.7072C12.3163 16.0982 11.6842 16.0982 11.2933 15.7072L6.29325 10.7072C5.90225 10.3162 5.90225 9.68424 6.29325 9.29324C6.68425 8.90224 7.31625 8.90224 7.70725 9.29324L12.0002 13.5862L16.2933 9.29324C16.6842 8.90224 17.3162 8.90224 17.7072 9.29324C17.9022 9.48824 18.0002 9.74424 18.0002 10.0002C18.0002 10.2562 17.9022 10.5122 17.7072 10.7072Z"
              fill="#2E2E3A"
            />
          </svg>
        </span>
      )}
    </button>
  );
}
